import { FieldError } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import kebabCase from 'lodash/kebabCase';
import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from 'react';
import Icon from '../Icon';
import InfoTooltip from '../InfoTooltip';
import Notify from '../Notify';
import Required from './Required';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  id?: string;
  label: string;
  value?: string | number;
  onChange?: (value: string) => void;
  error?: string;
  type?: 'string' | 'number' | 'email';
  isRequired?: boolean;
  tooltipText?: ReactNode;
  canCopy?: boolean;
  /** A control shown inside the field's right edge, such as an icon button. */
  endAction?: ReactNode;
  'data-testid'?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id = Math.random().toString(),
      error,
      type = 'text',
      tooltipText,
      isRequired,
      canCopy = false,
      endAction,
      'data-testid': testId,
      className,
      ...props
    },
    ref,
  ) => {
    const errorId = useId();
    const hasError = !isEmpty(error);
    return (
      <div className="flex w-full flex-col gap-1 font-noto">
        <div className="flex items-center gap-1 leading-snug">
          <label htmlFor={id} className="text-xs capitalize">
            <span>{label}</span>
            <Required isRequired={isRequired} />
          </label>
          {tooltipText && <InfoTooltip text={tooltipText} label={`About ${label}`} />}
        </div>
        <div className="group relative flex w-full rounded-sm">
          <input
            {...props}
            data-testid={testId}
            ref={ref}
            id={id}
            aria-invalid={hasError || undefined}
            aria-describedby={[props['aria-describedby'], hasError && errorId].filter(Boolean).join(' ') || undefined}
            type={type}
            aria-required={isRequired || undefined}
            className={classNames(
              'h-7 px-2 py-1 leading-[1.4] border-grey-30 w-full rounded-sm border p-1 text-xs font-noto outline-none focus:border-focus data-[has-error=true]:border-red-500',
              props.disabled && 'bg-[#efefef4d]!',
              !!endAction && 'pr-8',
              className,
            )}
            data-has-error={hasError}
            value={props.value}
            onChange={(e) => props.onChange?.(e.target.value)}
          />
          {!!endAction && (
            <div className={classNames('absolute inset-y-0 flex items-center', hasError ? 'right-3' : 'right-1')}>
              {endAction}
            </div>
          )}
          {hasError && <span aria-hidden="true" className="block w-2 shrink-0 bg-red-500" />}
          {canCopy && (
            <button
              type="button"
              aria-label={label ? `Copy ${label}` : 'Copy to clipboard'}
              data-testid={testId ? `${testId}-copy` : 'input-copy-button'}
              className="pointer-events-none absolute right-0 flex h-full cursor-pointer appearance-none items-center rounded border-0 bg-gray-100 p-2 opacity-0 focus-ring focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
              onClick={() => {
                navigator.clipboard.writeText(props.value?.toString() || '');
                Notify.info({
                  message: `"${props.value?.toString()}" - copied to Clipboard`,
                });
              }}
            >
              <Icon icon="copy" size={16} />
            </button>
          )}
        </div>
        {hasError && (
          <FieldError id={errorId} className="text-xs" data-testid={`${testId ?? kebabCase(label)}-error-details`}>
            {error}
          </FieldError>
        )}
      </div>
    );
  },
);

export default Input;
